
import window from 'bits/global'
import { trackEvent } from 'bits/pepin'
import { Component, Getter, namespace, Prop, State, Vue } from 'bits/vue'
import { observeVisualViewportSize } from 'bits/window'
import OzAvatar from 'library/v4/components/OzAvatar.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzIconButton, { OzIconButtonColorScheme, OzIconButtonSizePreset } from 'library/v4/components/OzIconButton.vue'
import type { Cid, Comment, CommentId, Id, Post, User } from 'types'
import MultilineInput from 'vuecomponents/multiline_input.vue'
import SurfacePostComment from 'vuecomponents/SurfacePostComment.vue'

@Component({
  components: {
    MultilineInput,
    OzAvatar,
    OzDivider,
    OzIcon,
    OzIconButton,
    SurfacePostComment,
  },
})
export default class SurfacePanelPostExpandedTextPanelComments extends Vue {
  $refs!: {
    addCommentInput: MultilineInput
  }

  OzDividerColorScheme = OzDividerColorScheme
  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzIconButtonSizePreset = OzIconButtonSizePreset

  /**
   * Whether to make the comment input field stick to the bottom of the
   * screen. This only affects mobile interface, where the comments are
   * displayed in a draggable tray.
   */
  @Prop({ default: false }) shouldCommentInputStickToScreen!: boolean

  @Getter isCommentable!: boolean
  @State user!: User
  @namespace('browser').Getter isPhone!: boolean
  @namespace('browser').Getter isAndroid!: boolean
  @namespace('browser').State darkMode!: true | false | 'auto'
  @namespace('comment').Getter isCommenting!: boolean
  @namespace('comment').Getter commentIdsByPost!: Record<number, string[] | undefined>
  @namespace('comment').Getter newCommentBodyForPostCid!: (postCid: Cid) => string
  @namespace('comment').State commentBeingEditedId!: CommentId
  @namespace('comment').State commentEntities!: Comment[]
  @namespace('comment').Action startWritingNewComment!: (payload: { postId: Id }) => void
  @namespace('comment').Action stopWritingNewComment!: (payload: { postId: Id }) => void
  @namespace('comment').Action updateInlineCommentInputLastTypedInMs!: () => void
  @namespace('comment').Action createComment!: (payload: { postId: Id; body: string }) => void
  @namespace('comment').Action updateNewCommentBody!: (payload: { postCid: Cid; body: string }) => void
  @namespace('comment').Action showCommentActionMenu!: (payload: { commentId: CommentId }) => void
  @namespace('comment').Action stopEditingComment!: () => void
  @namespace('comment').Action editComment!: (payload: { id: CommentId; body: string }) => void
  @namespace('postAction').Getter('expandedPost') post!: Post & { id: Id }
  @namespace('postAction').Getter('shouldFocusExpandedPostAddCommentInput') shouldFocusInput!: boolean

  isInputFocused = false
  initialVisualViewportHeight = 0
  visualViewportHeight = 0
  unobserveVisualViewportSize: (() => void) | null = null

  get isInputFocusedOnAndroid(): boolean {
    return this.isAndroid && this.isInputFocused
  }

  get shouldCommentInputStick(): boolean {
    return this.isPhone && this.shouldCommentInputStickToScreen
  }

  get commentList(): Comment[] {
    const commentIds: CommentId[] = this.commentIdsByPost[this.post.id] || []
    return commentIds.map((id) => this.commentEntities[id])
  }

  get newestCommentId(): CommentId {
    return this.commentList[this.commentList.length - 1]?.id
  }

  get newCommentBody(): string {
    return this.newCommentBodyForPostCid(this.post.cid)
  }

  isLastComment(index: number): boolean {
    return index === this.commentList.length - 1
  }

  onAddCommentClick(): void {
    this.isInputFocused = true
    this.startWritingNewComment({ postId: this.post.id })
  }

  onAddCommentBlur(): void {
    this.isInputFocused = false
    this.stopWritingNewComment({ postId: this.post.id })
  }

  onAddCommentInput(body: string): void {
    this.updateNewCommentBody({ postCid: this.post.cid, body })
    this.updateInlineCommentInputLastTypedInMs()
  }

  scrollToNewestComment(): void {
    setTimeout(() => {
      const newestCommentEl = document.getElementById(`comment-in-expanded-view-${this.newestCommentId}`)
      newestCommentEl?.scrollIntoView()
    }, 0)
  }

  publishComment(): void {
    if (!this.newCommentBody) return
    this.createComment({
      postId: this.post.id,
      body: this.newCommentBody,
    })
    this.scrollToNewestComment()
    this.updateNewCommentBody({ postCid: this.post.cid, body: '' })
    this.$refs.addCommentInput.focus()
    trackEvent('keyboard', { target: 'expanded-view-add-comment' })
  }

  mounted(): void {
    if (this.shouldFocusInput) this.$refs.addCommentInput.focus()

    // On Android, when the virtual keyboard is shown, the comment box doesn't move up
    // but instead is hidden under the keyboard (it resizes the visual viewport when this
    // happens btw). To fix it, we need to set `bottom` to a suitable number as if the
    // visual viewport is not resized. This can be done by storing the initial visual
    // viewport height, can subtract the current visual viewport height when the keyboard
    // is shown.
    if (this.isAndroid) {
      this.initialVisualViewportHeight = window.visualViewport.height
      this.visualViewportHeight = window.visualViewport.height
      this.unobserveVisualViewportSize = observeVisualViewportSize(({ height }) => {
        this.visualViewportHeight = this.isInputFocused ? height : this.initialVisualViewportHeight
      })
    }
  }

  beforeDestroy(): void {
    if (this.unobserveVisualViewportSize) this.unobserveVisualViewportSize()
  }
}
